import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { captureException } from "@sentry/react";
import { DEFAULT_LOCALE, loadLocaleResource } from "./config";
import type { Locale, TranslationResource } from "./config";

const syncDocumentLang = (locale: Locale) => {
  document.documentElement.lang = locale;
};

/** Load the active locale + EN fallback, then init i18next. Call once, before render. */
export const initI18n = async (initial: Locale): Promise<void> => {
  const toLoad = Array.from(new Set<Locale>([initial, DEFAULT_LOCALE]));
  const resources: Record<string, { translation: TranslationResource }> = {};
  const loaded = new Set<Locale>();
  await Promise.all(
    toLoad.map(async (locale) => {
      try {
        resources[locale] = { translation: await loadLocaleResource(locale) };
        loaded.add(locale);
      } catch (error) {
        // A chunk that fails to load must not abort the boot — render gates on
        // this, so throwing would leave the page blank. Worst case the UI comes
        // up in English, or in raw keys if EN itself is the one that failed.
        captureException(error);
      }
    }),
  );

  const lng = loaded.has(initial) ? initial : DEFAULT_LOCALE;

  await i18n.use(initReactI18next).init({
    lng,
    fallbackLng: DEFAULT_LOCALE,
    resources,
    interpolation: { escapeValue: false }, // React already escapes
    returnNull: false,
  });

  syncDocumentLang(lng);
};

/**
 * Switch language at runtime, lazy-loading the locale chunk if not yet present.
 * Rejects if the chunk can't be loaded — the caller must not persist the choice.
 */
export const changeAppLanguage = async (locale: Locale): Promise<void> => {
  if (!i18n.hasResourceBundle(locale, "translation")) {
    i18n.addResourceBundle(
      locale,
      "translation",
      await loadLocaleResource(locale),
    );
  }
  await i18n.changeLanguage(locale);
  syncDocumentLang(locale);
};

export default i18n;
